'use client'

// Awards — Chapter Three · Recognized Ground
//
// LAYER STACK
//   0  cream gradient background  (static)
//   2  grain overlay
//   3  acts (absolute, inset 0, overflow hidden)
//      Each award act has two panels that slide in from opposite edges.
//   60 scan line
//   40 progress rail

import { useRef, CSSProperties } from 'react'
import Image from 'next/image'
import { useTranslations } from 'next-intl'
import { useAwardsCinematic } from '@/lib/animations/useAwardsCinematic'

// ── Tokens ────────────────────────────────────────────────────────────────────
const PAPER       = '#F3EAC8'
const EARTH_INK   = '#2A1E06'
const EARTH_MID   = 'rgba(42,30,6,0.72)'
const EARTH_LOW   = 'rgba(42,30,6,0.52)'
const EARTH_FAINT = 'rgba(42,30,6,0.34)'
const OLIVE       = '#5B7005'
const OLIVE_SOFT  = 'rgba(91,112,5,0.48)'
const LIME        = '#9FAF08'
const FONT_BODY   = 'var(--font-body)'
const FONT_SERIF  = 'var(--font-serif)'
const FONT_DISP   = 'var(--font-display)'

// ── Stamp ─────────────────────────────────────────────────────────────────────
function Stamp({ text, id, tone = 'earth' }: { text: string; id: string; tone?: 'earth' | 'olive' }) {
  const stroke = tone === 'olive' ? OLIVE : 'rgba(81,50,5,0.9)'
  return (
    <svg viewBox="0 0 140 140" aria-hidden="true" style={{ width: '100%', height: '100%', display: 'block' }}>
      <defs>
        <path id={`aw-sp-${id}`} d="M 70,70 m -54,0 a 54,54 0 1,1 108,0 a 54,54 0 1,1 -108,0" />
      </defs>
      <circle cx="70" cy="70" r="62" fill="none" stroke={stroke} strokeWidth="1" opacity="0.85" />
      <circle cx="70" cy="70" r="54" fill="none" stroke={stroke} strokeWidth="0.7" strokeDasharray="2 3" opacity="0.7" />
      <circle cx="70" cy="70" r="38" fill="none" stroke={stroke} strokeWidth="0.8" opacity="0.75" />
      <text fill={stroke} opacity="0.9" style={{ fontFamily: 'Inter,sans-serif', fontSize: '9px', letterSpacing: '0.3em', fontWeight: 500 }}>
        <textPath href={`#aw-sp-${id}`} startOffset="0">{text}</textPath>
      </text>
      <text x="70" y="68" textAnchor="middle" fill={stroke} opacity="0.85" style={{ fontFamily: FONT_SERIF, fontStyle: 'italic', fontSize: '22px' }}>BS</text>
      <text x="70" y="84" textAnchor="middle" fill={stroke} opacity="0.7" style={{ fontFamily: 'Inter,sans-serif', fontSize: '7.5px', letterSpacing: '0.28em', fontWeight: 500 }}>2025</text>
    </svg>
  )
}

// ── Intro ─────────────────────────────────────────────────────────────────────
function ActIntro() {
  const t = useTranslations('awards')
  return (
    <div data-aw-act="intro" style={ACT_ROOT}>
      <div style={{
        position: 'absolute', inset: 0,
        display: 'flex', flexDirection: 'column', justifyContent: 'center',
        padding: 'clamp(56px,8vw,120px)',
      }}>
        <div data-aw-item style={{
          display: 'flex', alignItems: 'baseline', gap: 20, marginBottom: 32,
        }}>
          <span style={{ fontFamily: FONT_SERIF, fontStyle: 'italic', fontSize: 52, lineHeight: 1, letterSpacing: '-0.02em', color: OLIVE }}>03</span>
          <span style={{ width: 80, height: 1, background: OLIVE_SOFT, display: 'inline-block' }} />
          <span style={{ fontSize: 12, letterSpacing: '0.4em', textTransform: 'uppercase', fontWeight: 600, color: EARTH_INK, fontFamily: FONT_BODY } as CSSProperties}>
            {t('introChapter')}
          </span>
        </div>

        <h2 data-aw-title style={{
          fontFamily: FONT_DISP, fontWeight: 400,
          fontSize: 'clamp(60px,7vw,136px)', lineHeight: 0.94,
          letterSpacing: '-0.035em', color: EARTH_INK,
          maxWidth: '17ch', margin: '0 0 28px',
        }}>
          {t('introHeadlinePre')}{' '}
          <em style={{ fontFamily: FONT_SERIF, fontStyle: 'italic', color: OLIVE }}>{t('introHeadlineItalic')}</em>{' '}
          {t('introHeadlinePost')}
        </h2>

        <p data-aw-item style={{
          fontSize: 'clamp(16px,1.2vw,20px)', lineHeight: 1.55, color: EARTH_MID,
          maxWidth: '44ch', margin: 0, fontFamily: FONT_BODY,
        }}>
          {t('introCopy')}
        </p>
      </div>
    </div>
  )
}

// ── Award act ─────────────────────────────────────────────────────────────────
// layout:
//   'h-nl-pr'  horizontal — numeral panel LEFT,  photo panel RIGHT
//   'h-pl-nr'  horizontal — photo panel LEFT,    numeral panel RIGHT
//   'v-nt-pb'  vertical   — numeral panel TOP,   photo panel BOTTOM

type AwardActProps = {
  actId: string
  n: string
  label: string
  date: string
  title: string
  org: string
  context: string
  source: string
  imgSrc: string
  imgAlt: string
  stampId: string
  stampText: string
  stampTone?: 'earth' | 'olive'
  stampRot?: number
  layout: 'h-nl-pr' | 'h-pl-nr' | 'v-nt-pb'
}

function AwardAct({
  actId, n, label, date, title, org, context, source,
  imgSrc, imgAlt, stampId, stampText, stampTone = 'earth',
  stampRot = 7, layout,
}: AwardActProps) {
  const isV = layout === 'v-nt-pb'

  // Which side the numeral panel sits
  const numPanelSide = isV ? 'top' : layout === 'h-nl-pr' ? 'left' : 'right'
  const imgPanelSide = isV ? 'bottom' : layout === 'h-nl-pr' ? 'right' : 'left'

  const numPanelStyle: CSSProperties = {
    position: 'absolute',
    ...(isV
      ? { top: 0, left: 0, right: 0, height: '46%' }
      : numPanelSide === 'left'
        ? { left: 0, top: 0, bottom: 0, width: '50%' }
        : { right: 0, top: 0, bottom: 0, width: '50%' }),
    background: PAPER,
    display: 'flex',
    flexDirection: isV ? 'row' : 'column',
    alignItems: isV ? 'center' : 'flex-start',
    justifyContent: 'center',
    gap: isV ? 'clamp(32px,4vw,72px)' : 0,
    // V layout's numPanel sits flush with the viewport top, so the
    // eyebrow can crowd against the navbar pill (which ends at ~76px).
    // Bumping its top padding to ≥80px keeps content clear of the nav.
    padding: isV
      ? 'clamp(96px,10vh,128px) clamp(56px,7vw,112px) clamp(28px,3vw,56px)'
      : 'clamp(48px,5vw,88px) clamp(44px,4.5vw,80px)',
    overflow: 'hidden',
    zIndex: 2,
  }

  const imgPanelStyle: CSSProperties = {
    position: 'absolute',
    ...(isV
      ? { bottom: 0, left: 0, right: 0, height: '54%' }
      : imgPanelSide === 'right'
        ? { right: 0, top: 0, bottom: 0, width: '50%' }
        : { left: 0, top: 0, bottom: 0, width: '50%' }),
    background: '#2A1E06',
    overflow: 'hidden',
    zIndex: 2,
  }

  const decorNumStyle: CSSProperties = {
    position: isV ? 'relative' : 'absolute',
    ...(isV ? {} : { bottom: '-0.1em', right: '-0.04em' }),
    fontFamily: FONT_DISP,
    fontSize: isV ? 'clamp(100px,14vw,200px)' : 'clamp(200px,26vw,420px)',
    fontWeight: 700,
    color: OLIVE,
    opacity: 0.07,
    lineHeight: 1,
    letterSpacing: '-0.05em',
    userSelect: 'none',
    pointerEvents: 'none',
    flexShrink: 0,
  }

  const textBlock = (
    <div style={{ position: 'relative', zIndex: 1, flex: isV ? '1 1 auto' : undefined }}>
      <div data-aw-item style={{
        display: 'flex', alignItems: 'center', gap: 10,
        fontSize: 11, letterSpacing: '0.4em',
        textTransform: 'uppercase' as const, fontWeight: 600, color: EARTH_LOW,
        fontFamily: FONT_BODY, marginBottom: 14,
      }}>
        <span style={{ fontFamily: FONT_SERIF, fontStyle: 'italic', fontSize: 19, letterSpacing: '-0.02em', color: OLIVE, textTransform: 'none' as const }}>{n}</span>
        <span style={{ width: 24, height: 1, background: OLIVE_SOFT, display: 'inline-block' }} />
        <span>{label}</span>
        <span style={{ color: EARTH_FAINT }}>·</span>
        <span>{date}</span>
      </div>

      <h3 data-aw-item style={{
        fontFamily: FONT_DISP, fontWeight: 600,
        fontSize: isV ? 'clamp(26px,2.8vw,46px)' : 'clamp(34px,3.8vw,64px)',
        lineHeight: 1.05, letterSpacing: '-0.025em', color: EARTH_INK,
        margin: '0 0 12px', maxWidth: '14ch',
      }}>{title}</h3>

      <p data-aw-item style={{
        fontFamily: FONT_SERIF, fontStyle: 'italic',
        fontSize: isV ? 'clamp(13px,1.1vw,17px)' : 'clamp(14px,1.2vw,19px)',
        color: OLIVE, margin: '0 0 8px', maxWidth: '26ch',
      }}>{org}</p>

      <p data-aw-item style={{
        fontSize: isV ? 'clamp(12px,0.9vw,15px)' : 'clamp(13px,0.95vw,16px)',
        lineHeight: 1.5, color: EARTH_MID, maxWidth: '40ch',
        margin: '0 0 12px', fontFamily: FONT_BODY,
      }}>{context}</p>

      <div data-aw-item style={{
        fontSize: 9.5, letterSpacing: '0.38em',
        textTransform: 'uppercase' as const, color: EARTH_FAINT, fontFamily: FONT_BODY,
      }}>{source}</div>
    </div>
  )

  return (
    <div data-aw-act={actId} style={ACT_ROOT}>

      {/* Numeral panel */}
      <div data-aw-panel={numPanelSide} style={numPanelStyle}>
        {isV
          ? <><span aria-hidden="true" style={decorNumStyle}>{n}</span>{textBlock}</>
          : <>{textBlock}<span aria-hidden="true" style={decorNumStyle}>{n}</span></>
        }
      </div>

      {/* Photo panel */}
      <div data-aw-panel={imgPanelSide} style={imgPanelStyle}>
        {/* Stamp */}
        <div data-aw-stamp data-stamp-rot={stampRot} style={{
          position: 'absolute', top: 24, right: 24,
          width: 112, height: 112, zIndex: 10,
          opacity: 0.8, filter: 'drop-shadow(0 4px 14px rgba(42,30,6,0.22))',
          pointerEvents: 'none',
        }}>
          <Stamp id={stampId} text={stampText} tone={stampTone} />
        </div>

        <Image
          src={imgSrc}
          alt={imgAlt}
          fill
          loading="lazy"
          sizes="(max-width: 768px) 100vw, 50vw"
          style={{ objectFit: 'cover', display: 'block' }}
        />
      </div>

      {/* Vertical divider (horizontal layouts only) */}
      {!isV && (
        <div aria-hidden="true" style={{
          position: 'absolute', top: 0, bottom: 0, left: '50%',
          width: 1, background: 'rgba(42,30,6,0.1)', zIndex: 5, pointerEvents: 'none',
        }} />
      )}
    </div>
  )
}

// ── Closing act ───────────────────────────────────────────────────────────────
function ActClose() {
  const t = useTranslations('awards')
  return (
    <div data-aw-act="close" style={ACT_ROOT}>
      <div style={{
        position: 'absolute', inset: 0,
        display: 'flex', flexDirection: 'column',
        justifyContent: 'center', alignItems: 'center', textAlign: 'center',
        padding: 'clamp(56px,8vw,120px)',
      }}>
        <div data-aw-item style={{
          display: 'flex', alignItems: 'center', gap: 14,
          fontSize: 11, letterSpacing: '0.42em', textTransform: 'uppercase' as const,
          fontWeight: 600, color: EARTH_LOW, fontFamily: FONT_BODY, marginBottom: 32,
        }}>
          <span style={{ width: 44, height: 1, background: OLIVE_SOFT, display: 'inline-block' }} />
          <span>{t('closeLabel')}</span>
          <span style={{ width: 44, height: 1, background: OLIVE_SOFT, display: 'inline-block' }} />
        </div>

        <h3 data-aw-title style={{
          fontFamily: FONT_DISP, fontWeight: 400,
          fontSize: 'clamp(68px,7.2vw,132px)', lineHeight: 0.96,
          letterSpacing: '-0.035em', color: EARTH_INK,
          margin: '0 0 24px', maxWidth: '16ch',
        }}>
          {t('closeHeadlinePre')}{' '}
          <em style={{ fontFamily: FONT_SERIF, fontStyle: 'italic', color: OLIVE }}>{t('closeHeadlineItalic')}</em>
        </h3>

        <p data-aw-item style={{
          fontSize: 'clamp(16px,1.2vw,20px)', lineHeight: 1.55, color: EARTH_MID,
          margin: '0 0 36px', maxWidth: '40ch', fontFamily: FONT_BODY,
        }}>
          {t('closeCopy')}
        </p>

        <a data-aw-item href="#interlude" className="aw-close-cta" style={{
          display: 'inline-flex', alignItems: 'center', gap: 12,
          fontSize: 11, letterSpacing: '0.4em', textTransform: 'uppercase' as const,
          fontWeight: 600, color: EARTH_INK, textDecoration: 'none',
          borderBottom: `1px solid ${EARTH_LOW}`, paddingBottom: 8,
          fontFamily: FONT_BODY,
        }}>
          {t('closeCta')}{' '}
          <span aria-hidden="true" style={{ fontSize: 18, lineHeight: 1 }}>↓</span>
        </a>
      </div>
    </div>
  )
}

// ── Shared act root style ─────────────────────────────────────────────────────
const ACT_ROOT: CSSProperties = {
  position: 'absolute',
  inset: 0,
  overflow: 'hidden',
}

// ── Section root ──────────────────────────────────────────────────────────────
export type AwardItem = {
  /** Photo URL (CMS-resolved or fallback) */
  imgSrc: string
  imgAlt?: string
  title?: string
  organization?: string
  displayDate?: string
  description?: string
  sourceCitation?: string
  stampText?: string
}

// Per-position visual presentation — kept on the client because it doesn't
// belong in the CMS schema (typography / layout / rotation are designer
// choices, not editor content).
const ACT_LAYOUTS: Array<{
  layout: 'h-nl-pr' | 'h-pl-nr' | 'v-nt-pb'
  stampTone: 'earth' | 'olive'
  stampRot: number
}> = [
  { layout: 'h-nl-pr', stampTone: 'olive', stampRot: 8 },
  { layout: 'h-pl-nr', stampTone: 'earth', stampRot: -5 },
  { layout: 'v-nt-pb', stampTone: 'olive', stampRot: 6 },
]

// Fallback award imagery used when the CMS is unreachable. Mirrors the
// previously hardcoded values so the page never goes blank.
const FALLBACK_AWARDS: AwardItem[] = [
  { imgSrc: '/HOME/Premios/20250708_191846.jpg' },
  { imgSrc: '/HOME/Premios/54579287501_1c5e5c6db5_o.jpg' },
  { imgSrc: '/HOME/Premios/IMG-20251209-WA0008.jpg' },
]

export default function AwardsClient({ awards }: { awards?: AwardItem[] } = {}) {
  const rootRef = useRef<HTMLElement>(null)
  const t = useTranslations('awards')
  useAwardsCinematic(rootRef)
  const items = awards && awards.length > 0 ? awards : FALLBACK_AWARDS

  return (
    <section
      ref={rootRef}
      id="awards"
      className="aw-section"
      style={{ position: 'relative', background: PAPER, color: EARTH_INK, fontFamily: FONT_BODY, isolation: 'isolate' }}
    >
      <div
        data-aw-viewport
        style={{ position: 'relative', height: '100vh', overflow: 'hidden' }}
      >
        {/* Static cream background */}
        <div aria-hidden="true" style={{
          position: 'absolute', inset: 0, zIndex: 0,
          background: 'linear-gradient(160deg, #F7F0D8 0%, #F3EAC8 40%, #EBE0B5 100%)',
        }} />

        {/* Grain */}
        <div aria-hidden="true" style={{
          position: 'absolute', inset: 0,
          backgroundImage: 'url(/grain.svg)', backgroundSize: '180px 180px',
          opacity: 0.09, mixBlendMode: 'multiply', zIndex: 55, pointerEvents: 'none',
        }} />

        {/* Scan line */}
        <div data-aw-scan aria-hidden="true" style={{
          position: 'absolute', left: 0, right: 0, top: 0,
          height: 2, background: LIME, boxShadow: `0 0 16px ${LIME}`,
          zIndex: 60, pointerEvents: 'none', opacity: 0,
        }} />

        {/* Acts */}
        <ActIntro />

        {items.map((item, idx) => {
          const n = String(idx + 1).padStart(2, '0')
          // Cycle through the 3 designer-chosen layouts so adding a 4th, 5th… award via CMS works.
          const visual = ACT_LAYOUTS[idx % ACT_LAYOUTS.length]
          return (
            <AwardAct
              key={n}
              actId={n}
              n={n}
              label={t(`act${n}Label`)}
              date={item.displayDate ?? t(`act${n}Date`)}
              title={item.title ?? t(`act${n}Title`)}
              org={item.organization ?? t(`act${n}Org`)}
              context={item.description ?? t(`act${n}Context`)}
              source={item.sourceCitation ?? t(`act${n}Source`)}
              imgSrc={item.imgSrc}
              imgAlt={item.imgAlt ?? t(`act${n}ImgAlt`)}
              stampId={n}
              stampText={item.stampText ?? t(`act${n}StampText`)}
              stampTone={visual.stampTone}
              stampRot={visual.stampRot}
              layout={visual.layout}
            />
          )
        })}

        <ActClose />

      </div>

      <style>{`
        .aw-close-cta:hover { color: ${OLIVE}; border-color: ${OLIVE}; }

        @media (max-width: 767px), (hover: none) and (pointer: coarse) {
          .aw-section [data-aw-viewport] {
            height: auto !important;
            overflow: visible !important;
          }
          .aw-section [data-aw-act] {
            position: relative !important;
            inset: auto !important;
            min-height: 80vw !important;
            overflow: hidden !important;
            transform: none !important;
            opacity: 1 !important;
            visibility: visible !important;
          }
          .aw-section [data-aw-act="intro"],
          .aw-section [data-aw-act="close"] {
            min-height: 100svh !important;
            padding: 80px 28px !important;
          }
          .aw-section [data-aw-panel] {
            position: relative !important;
            width: 100% !important;
            height: 56vw !important;
            left: auto !important;
            right: auto !important;
            top: auto !important;
            bottom: auto !important;
            transform: none !important;
          }
          .aw-section [data-aw-panel="left"],
          .aw-section [data-aw-panel="right"] {
            height: auto !important;
            min-height: 52vw !important;
            padding: 48px 24px !important;
          }
          .aw-section [data-aw-panel="top"] {
            height: auto !important;
            min-height: 60vw !important;
            padding: 48px 24px !important;
            flex-direction: column !important;
          }
          .aw-section [data-aw-scan] { display: none !important; }
          .aw-section [data-aw-stamp] {
            width: 76px !important;
            height: 76px !important;
          }

          /* Close act headline scales down on mobile */
          .aw-section [data-aw-act="close"] [data-aw-title] {
            font-size: clamp(36px, 9.4vw, 56px) !important;
            margin-bottom: 20px !important;
          }
          .aw-section [data-aw-act="close"] p[data-aw-item] {
            font-size: 14px !important;
            margin-bottom: 28px !important;
          }
          .aw-section [data-aw-act="close"] [data-aw-item]:first-child {
            font-size: 9px !important;
            letter-spacing: 0.32em !important;
            margin-bottom: 22px !important;
            gap: 10px !important;
          }
          .aw-section [data-aw-act="close"] [data-aw-item]:first-child > span:first-child,
          .aw-section [data-aw-act="close"] [data-aw-item]:first-child > span:last-child {
            width: 28px !important;
          }

          /* Intro act eyebrow + headline tighten too */
          .aw-section [data-aw-act="intro"] [data-aw-title] {
            font-size: clamp(36px, 9.4vw, 56px) !important;
          }
        }
      `}</style>
    </section>
  )
}
